import {
  createContextScope
} from "../../../chunk-MKBCGKOX.mjs";
import {
  useLayoutEffect2
} from "../../../chunk-OROA335X.mjs";
import {
  cva
} from "../../../chunk-6A7LB5UJ.mjs";
import "../../../chunk-QJVDQ4DQ.mjs";
import {
  Primitive
} from "../../../chunk-7TMHQ6XO.mjs";
import {
  useCallbackRef
} from "../../../chunk-BQ72SK4G.mjs";
import "../../../chunk-FBWCVJGC.mjs";
import "../../../chunk-D57E6H3M.mjs";

// ../../node_modules/.pnpm/@radix-ui+react-avatar@1.2.0_@types+react-dom@19.2.3_@types+react@19.2.14__@types+react_8f2abded34bc714745d1adfffbcafd80/node_modules/@radix-ui/react-avatar/dist/index.mjs
import * as React from "react";
import { jsx } from "react/jsx-runtime";
var AVATAR_NAME = "Avatar";
var [createAvatarContext, createAvatarScope] = createContextScope(AVATAR_NAME);
var STATIC_IMAGE_COUNT_STATE = [
  0,
  () => void 0
];
var [AvatarProvider, useAvatarContext] = createAvatarContext(AVATAR_NAME);
var Avatar = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeAvatar, ...avatarProps } = props;
    const [imageLoadingStatus, setImageLoadingStatus] = React.useState("idle");
    const [imageCount, setImageCount] = useImageCount();
    return /* @__PURE__ */ jsx(
      AvatarProvider,
      {
        scope: __scopeAvatar,
        imageLoadingStatus,
        setImageLoadingStatus,
        imageCount,
        setImageCount,
        children: /* @__PURE__ */ jsx(Primitive.span, { ...avatarProps, ref: forwardedRef })
      }
    );
  }
);
Avatar.displayName = AVATAR_NAME;
var IMAGE_NAME = "AvatarImage";
var AvatarImage = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeAvatar, src, onLoadingStatusChange, ...imageProps } = props;
    const context = useAvatarContext(IMAGE_NAME, __scopeAvatar);
    useUpdateImageCount(context.setImageCount);
    const imageLoadingStatus = useImageLoadingStatus(src, {
      referrerPolicy: imageProps.referrerPolicy,
      crossOrigin: imageProps.crossOrigin,
      loadingStatus: context.imageLoadingStatus,
      setLoadingStatus: context.setImageLoadingStatus
    });
    const handleLoadingStatusChange = useCallbackRef((status) => {
      onLoadingStatusChange?.(status);
    });
    const loadingStatusRef = React.useRef(imageLoadingStatus);
    useLayoutEffect2(() => {
      const previousLoadingStatus = loadingStatusRef.current;
      loadingStatusRef.current = imageLoadingStatus;
      if (imageLoadingStatus !== previousLoadingStatus) {
        handleLoadingStatusChange(imageLoadingStatus);
      }
    }, [imageLoadingStatus, handleLoadingStatusChange]);
    return imageLoadingStatus === "loaded" ? /* @__PURE__ */ jsx(Primitive.img, { ...imageProps, ref: forwardedRef, src }) : null;
  }
);
AvatarImage.displayName = IMAGE_NAME;
var FALLBACK_NAME = "AvatarFallback";
var AvatarFallback = React.forwardRef(
  (props, forwardedRef) => {
    const { __scopeAvatar, delayMs, ...fallbackProps } = props;
    const context = useAvatarContext(FALLBACK_NAME, __scopeAvatar);
    const [canRender, setCanRender] = React.useState(delayMs === void 0);
    React.useEffect(() => {
      if (delayMs !== void 0) {
        const timerId = window.setTimeout(() => setCanRender(true), delayMs);
        return () => window.clearTimeout(timerId);
      }
    }, [delayMs]);
    return canRender && context.imageLoadingStatus !== "loaded" ? /* @__PURE__ */ jsx(Primitive.span, { ...fallbackProps, ref: forwardedRef }) : null;
  }
);
AvatarFallback.displayName = FALLBACK_NAME;
function useImageLoadingStatus(src, {
  loadingStatus,
  setLoadingStatus,
  referrerPolicy,
  crossOrigin
}) {
  useLayoutEffect2(() => {
    if (!src) {
      setLoadingStatus("error");
      return;
    }
    const image = new window.Image();
    const handleLoad = (event) => {
      const image2 = event.currentTarget;
      setLoadingStatus(getImageLoadingStatus(image2));
    };
    const handleError = () => setLoadingStatus("error");
    image.addEventListener("load", handleLoad);
    image.addEventListener("error", handleError);
    if (referrerPolicy) {
      image.referrerPolicy = referrerPolicy;
    }
    image.crossOrigin = crossOrigin ?? null;
    image.src = src;
    setLoadingStatus(getImageLoadingStatus(image));
    return () => {
      image.removeEventListener("load", handleLoad);
      image.removeEventListener("error", handleError);
      setLoadingStatus("idle");
    };
  }, [src, crossOrigin, referrerPolicy, setLoadingStatus]);
  return loadingStatus;
}
function getImageLoadingStatus(image) {
  return image.complete ? image.naturalWidth > 0 ? "loaded" : "error" : "loading";
}
function useImageCount() {
  let state = STATIC_IMAGE_COUNT_STATE;
  if (true) {
    state = React.useState(0);
    const [imageCount] = state;
    const hasWarnedRef = React.useRef(false);
    React.useEffect(() => {
      if (imageCount > 1 && !hasWarnedRef.current) {
        hasWarnedRef.current = true;
        console.warn(
          "Avatar: Only one `Avatar.Image` component should be rendered per `Avatar.Root`, but multiple were detected. This will lead to unexpected behavior."
        );
      }
    }, [imageCount]);
  }
  return state;
}
function useUpdateImageCount(setImageCount) {
  if (true) {
    React.useEffect(() => {
      setImageCount((imageCount) => imageCount + 1);
      return () => {
        setImageCount((imageCount) => imageCount - 1);
      };
    }, [setImageCount]);
  }
}

// src/components/primitive/Avatar/Avatar.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var avatarVariants = cva("buyscout-avatar", {
  variants: {
    size: {
      sm: "buyscout-avatar--size-sm",
      md: "buyscout-avatar--size-md",
      lg: "buyscout-avatar--size-lg"
    },
    tone: {
      gradient: "buyscout-avatar--tone-gradient",
      gold: "buyscout-avatar--tone-gold",
      neutral: "buyscout-avatar--tone-neutral"
    },
    shape: {
      circle: "buyscout-avatar--shape-circle",
      rounded: "buyscout-avatar--shape-rounded"
    },
    // A logo is as often a wide wordmark as a square mark, and cropping one mid-word is worse
    // than letterboxing it, so logos take `contain`; photos and portraits fill with `cover`.
    fit: {
      cover: "buyscout-avatar--fit-cover",
      contain: "buyscout-avatar--fit-contain"
    }
  },
  defaultVariants: {
    size: "md",
    tone: "gradient",
    shape: "circle",
    fit: "cover"
  }
});
function Avatar2({
  ref,
  ...props
}) {
  const { src, alt = "", fallback, size, tone, shape, fit, className, ...rest } = props;
  const classes = avatarVariants({ size, tone, shape, fit, className });
  return /* @__PURE__ */ jsxs(Avatar, { ref, className: classes, ...rest, children: [
    src && /* @__PURE__ */ jsx2(AvatarImage, { className: "buyscout-avatar__image", src, alt }),
    /* @__PURE__ */ jsx2(AvatarFallback, { className: "buyscout-avatar__fallback", delayMs: src ? 200 : 0, children: fallback })
  ] });
}
Avatar2.displayName = "Avatar";
export {
  Avatar2 as Avatar
};
//# sourceMappingURL=Avatar.mjs.map